import { NOT_FRANKLIN_MADE_FILTER } from "@/domains/documents/franklinMade/franklinMade";
import { supabase } from "@/integrations/supabase/client";
import {
  applySignedContainerRowRule,
  fetchUnzippedSignedContainerIds,
} from "@/stores/documents/repository/queries";

import { applyDocumentRootTitles, isMissingRelation } from "@/stores/pm/repository";
import { profileDisplayName } from "@/stores/documents/repository/mappers";

import type { DocumentCommentMentionRow, IssueCommentMentionRow, MentionRows } from "./forYou";
import type { RecentDocumentRow, RecentIssueActivityRow, RecentRows } from "./summary";

/** PostgREST's max-rows is configurable; use the same 500-row pages as PM. */
const PAGE_SIZE = 500;

/**
 * Rows read per source before giving up. A week of one project rarely comes
 * near it, but a bulk upload or a bulk status edit can; past the cap the
 * summary reports "at least" for the windows the fetch did not reach.
 */
export const RECENT_CHANGES_ROW_CAP = 20_000;

interface Cursor {
  id: number;
  created_at: string;
}

/**
 * Read every row newer than `since`, newest-first, by keyset on
 * (created_at, id) rather than by offset: the window is still open while the
 * loop pages, and a row inserted mid-read would shift an offset page and read
 * one row twice. Same scheme as the PM protocol export.
 */
async function readNewestFirst<T extends Cursor>(
  page: (
    cursor: Cursor | undefined,
    limit: number
  ) => PromiseLike<{
    data: unknown;
    error: { message: string } | null;
  }>
): Promise<RecentRows<T>> {
  const rows: T[] = [];
  for (;;) {
    const remaining = RECENT_CHANGES_ROW_CAP - rows.length;
    if (remaining <= 0) return { rows, truncated: true };
    const limit = Math.min(PAGE_SIZE, remaining);
    const { data, error } = await page(rows.at(-1), limit);
    if (error) throw new Error(error.message);
    const batch = (data ?? []) as T[];
    rows.push(...batch);
    if (batch.length < limit) return { rows, truncated: false };
  }
}

/** The (created_at, id) keyset predicate. Quoted: a timestamptz carries `:` and `+`. */
function afterCursor(cursor: Cursor): string {
  return `created_at.lt."${cursor.created_at}",and(created_at.eq."${cursor.created_at}",id.lt.${cursor.id})`;
}

/**
 * Every PM activity row in the project since `since`.
 *
 * The project is matched on the issue's CURRENT project through an inner join,
 * not on the row's own `building_project_id` (a snapshot of where the issue sat
 * when the row was written) — an issue moved into this project brings its
 * history with it, as on the Issues page. The join also drops rows whose issue
 * is soft-deleted or unreadable under RLS, so a deleted issue's churn is not
 * reported as change.
 */
export async function fetchRecentIssueActivity(
  buildingId: number,
  projectId: number,
  since: string
): Promise<RecentRows<RecentIssueActivityRow>> {
  const result = await readNewestFirst<RecentIssueActivityRow>((cursor, limit) => {
    let query = supabase
      .from("pm_issue_activity")
      .select(
        "id, issue_id, action_type, actor_id, created_at, old_value, new_value, metadata, pm_issues!inner(building_project_id, issue_number, title, status)"
      )
      .eq("building_id", buildingId)
      .eq("pm_issues.building_project_id", projectId)
      .is("pm_issues.deleted_at", null)
      .gte("created_at", since);
    if (cursor) query = query.or(afterCursor(cursor));
    return query
      .order("created_at", { ascending: false })
      .order("id", { ascending: false })
      .limit(limit)
      .then(({ data, error }) => ({
        data: ((data ?? []) as ActivityWithIssue[]).map(({ pm_issues: issue, ...row }) => ({
          ...row,
          metadata: row.metadata ?? {},
          issue_number: issue.issue_number,
          issue_title: issue.title,
          issue_status: issue.status,
        })),
        error,
      }));
  });
  await applyIssueDisplayTitles(result.rows);
  return result;
}

type ActivityWithIssue = Omit<
  RecentIssueActivityRow,
  "issue_number" | "issue_title" | "issue_status"
> & {
  pm_issues: { building_project_id: number; issue_number: number; title: string; status: string };
};

/**
 * Swap stored issue titles for their viewer-language display titles (a
 * document's parent issue shows the filename), as the Notifications feed does.
 */
async function applyIssueDisplayTitles(
  rows: Array<{ issue_id: number; issue_title: string }>
): Promise<void> {
  const refs = new Map<number, { id: number; title: string }>();
  for (const row of rows) refs.set(row.issue_id, { id: row.issue_id, title: row.issue_title });
  if (refs.size === 0) return;
  const issues = [...refs.values()];
  await applyDocumentRootTitles(issues);
  const titles = new Map(issues.map((issue) => [issue.id, issue.title]));
  for (const row of rows) row.issue_title = titles.get(row.issue_id) ?? row.issue_title;
}

/**
 * Every document row added to the project since `since` that the documents
 * list would show: not trashed, not hidden, not a DWG's converted PDF, and
 * under the same container rule the list and the dashboard counts apply.
 * Rows Franklin filed itself (the brief's additions PDF) are not news and are
 * left out. Superseded versions count too — a document added and then revised in the
 * same week is one new document and one new version.
 */
export async function fetchRecentDocumentRows(
  buildingId: number,
  projectId: number,
  since: string
): Promise<RecentRows<RecentDocumentRow>> {
  const signedContainerIds = await fetchUnzippedSignedContainerIds(buildingId);
  return readNewestFirst<RecentDocumentRow>((cursor, limit) => {
    let query = supabase
      .from("documents")
      .select("id, created_at, version, filename, file_path, content_type, size, state")
      .eq("building_id", buildingId)
      .eq("project_id", projectId)
      .eq("is_deleted", false)
      .eq("hidden", false)
      .or("is_dwg_child.is.null,is_dwg_child.eq.false")
      .or(NOT_FRANKLIN_MADE_FILTER)
      .gte("created_at", since);
    query = applySignedContainerRowRule(query, signedContainerIds);
    if (cursor) query = query.or(afterCursor(cursor));
    return query
      .order("created_at", { ascending: false })
      .order("id", { ascending: false })
      .limit(limit);
  });
}

/**
 * Mentions newer than this are read per timeframe; the dashboard lists a
 * handful, so a cap well above that costs nothing and bounds a pathological
 * thread.
 */
const MENTION_LIMIT = 500;
/** Ids per `.in()` lookup; PostgREST URLs and max-rows both bound it. */
const LOOKUP_CHUNK = 200;

async function lookupInChunks<T>(
  ids: number[],
  read: (chunk: number[]) => PromiseLike<{ data: unknown; error: { message: string } | null }>
): Promise<T[]> {
  const out: T[] = [];
  for (let i = 0; i < ids.length; i += LOOKUP_CHUNK) {
    const { data, error } = await read(ids.slice(i, i + LOOKUP_CHUNK));
    if (error) throw new Error(error.message);
    out.push(...((data ?? []) as T[]));
  }
  return out;
}

interface IssueMentionJoin {
  comment_id: number;
  created_at: string;
  pm_issue_comments: {
    issue_id: number;
    body: string;
    author_id: number;
    pm_issues: { issue_number: number; title: string };
  };
}

interface DocumentMentionJoin {
  comment_id: number;
  created_at: string;
  document_comments: {
    thread_id: number;
    body: string;
    author_id: number;
    document_comment_threads: { document_id: number };
  };
}

/** Comments on this project's issues that mention `me`, newest first. */
async function fetchIssueCommentMentions(
  projectId: number,
  me: number,
  since: string
): Promise<IssueCommentMentionRow[]> {
  const { data, error } = await supabase
    .from("pm_issue_comment_mentions")
    .select(
      "comment_id, created_at, pm_issue_comments!inner(issue_id, body, author_id, deleted_at, pm_issues!inner(building_project_id, deleted_at, issue_number, title))"
    )
    .eq("mentioned_user_id", me)
    .gte("created_at", since)
    .is("pm_issue_comments.deleted_at", null)
    .eq("pm_issue_comments.pm_issues.building_project_id", projectId)
    .is("pm_issue_comments.pm_issues.deleted_at", null)
    .order("created_at", { ascending: false })
    .limit(MENTION_LIMIT);
  if (error) throw new Error(error.message);
  const rows = ((data ?? []) as IssueMentionJoin[]).map((row) => ({
    comment_id: row.comment_id,
    created_at: row.created_at,
    issue_id: row.pm_issue_comments.issue_id,
    issue_number: row.pm_issue_comments.pm_issues.issue_number,
    issue_title: row.pm_issue_comments.pm_issues.title,
    body: row.pm_issue_comments.body,
    author_id: row.pm_issue_comments.author_id,
  }));
  await applyIssueDisplayTitles(rows);
  return rows;
}

/**
 * Comments that mention `me` on this project's documents in threads that were
 * NOT filed as issues. A thread filed as an issue (`linked_issue_id` set —
 * including one promoted after the fact, whose document-side copies are left
 * behind) keeps its conversation in `pm_issue_comments`, so it is read there
 * and skipped here rather than counted twice.
 */
async function fetchDocumentCommentMentions(
  projectId: number,
  me: number,
  since: string
): Promise<DocumentCommentMentionRow[]> {
  const { data, error } = await supabase
    .from("document_comment_mentions")
    .select(
      "comment_id, created_at, document_comments!inner(thread_id, body, author_id, deleted_at, document_comment_threads!inner(project_id, document_id, linked_issue_id, deleted_at))"
    )
    .eq("mentioned_user_id", me)
    .gte("created_at", since)
    .is("document_comments.deleted_at", null)
    .eq("document_comments.document_comment_threads.project_id", projectId)
    .is("document_comments.document_comment_threads.linked_issue_id", null)
    .is("document_comments.document_comment_threads.deleted_at", null)
    .order("created_at", { ascending: false })
    .limit(MENTION_LIMIT);
  if (error) {
    // Before the document comments schema applies, there are none to read.
    if (isMissingRelation(error)) return [];
    throw new Error(error.message);
  }
  return ((data ?? []) as DocumentMentionJoin[]).map((row) => ({
    comment_id: row.comment_id,
    created_at: row.created_at,
    thread_id: row.document_comments.thread_id,
    document_id: row.document_comments.document_comment_threads.document_id,
    body: row.document_comments.body,
    author_id: row.document_comments.author_id,
  }));
}

/**
 * Every comment in the project since `since` that mentions `me`, with what it
 * takes to label and link each one: which issues are really document threads,
 * the documents' filenames and the authors' names.
 *
 * `issues` false (no access to the project's issues) skips the issue-side
 * mentions — a document thread filed as an issue is an issue for access.
 */
export async function fetchMyMentions(
  projectId: number,
  me: number,
  since: string,
  { issues }: { issues: boolean }
): Promise<MentionRows> {
  const [issueComments, documentComments] = await Promise.all([
    issues ? fetchIssueCommentMentions(projectId, me, since) : Promise.resolve([]),
    fetchDocumentCommentMentions(projectId, me, since),
  ]);

  const issueIds = [...new Set(issueComments.map((row) => row.issue_id))];
  const threads = await lookupInChunks<{
    id: number;
    linked_issue_id: number;
    document_id: number;
  }>(issueIds, (chunk) =>
    supabase
      .from("document_comment_threads")
      .select("id, linked_issue_id, document_id")
      .in("linked_issue_id", chunk)
      .is("deleted_at", null)
  );
  const threadByIssueId = new Map(
    threads.map((t) => [t.linked_issue_id, { threadId: t.id, documentId: t.document_id }])
  );

  const documentIds = [
    ...new Set([
      ...threads.map((t) => t.document_id),
      ...documentComments.map((row) => row.document_id),
    ]),
  ];
  const documents = await lookupInChunks<{ id: number; filename: string }>(documentIds, (chunk) =>
    supabase.from("documents").select("id, filename").in("id", chunk)
  );

  const authorIds = [
    ...new Set([...issueComments, ...documentComments].map((row) => row.author_id)),
  ];
  const authors = await lookupInChunks<{
    id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  }>(authorIds, (chunk) =>
    supabase.from("user_profiles").select("id, first_name, last_name, email").in("id", chunk)
  );

  return {
    issueComments,
    documentComments,
    threadByIssueId,
    filenameByDocumentId: new Map(documents.map((d) => [d.id, d.filename])),
    authorNameById: new Map(authors.map((a) => [a.id, profileDisplayName(a) ?? a.email ?? ""])),
  };
}
